import { buildClinicPrintDocumentHtml } from './clinicPrintLetterhead';
import {
  PHYSIO_CONDUCT_OPTIONS,
  PHYSIO_SPECIFIC_GOALS,
  PHYSIO_THERAPY_MODALITIES,
} from '../constants/physiotherapyAssessmentSchema';
import {
  labelsFromPosturalKeys,
  posturalAsymmetryLabel,
  POSTURAL_ANTERIOR_LOWER_BODY,
  POSTURAL_LATERAL_FINDINGS,
  POSTURAL_PHOTO_VIEWS,
  POSTURAL_POSTERIOR_FINDINGS,
} from '../constants/posturalAssessmentSchema';
import type { PhysioInitialResponses, PhysioPostureAssessment, PhysioReEvaluationResponses } from '../types/physiotherapyAssessment';

function esc(v: unknown) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function row(label: string, value: unknown) {
  const v = String(value ?? '').trim();
  if (!v) return '';
  return `<tr><td style="padding:4px 8px;font-weight:600;width:38%;vertical-align:top;border-bottom:1px solid #f1f5f9;">${esc(label)}</td><td style="padding:4px 8px;border-bottom:1px solid #f1f5f9;">${esc(v)}</td></tr>`;
}

function section(title: string, rowsHtml: string) {
  if (!rowsHtml.trim()) return '';
  return `<div style="margin:0 0 14px;">
    <div style="font-size:11px;font-weight:800;color:#0f766e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;border-bottom:2px solid #ccfbf1;padding-bottom:4px;">${esc(title)}</div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;">${rowsHtml}</table>
  </div>`;
}

function labelsFromKeys(keys: string[], catalog: ReadonlyArray<{ key: string; label: string }>) {
  return keys.map((k) => catalog.find((c) => c.key === k)?.label || k).join(', ');
}

function triStateLabel(v: string) {
  const map: Record<string, string> = { normal: 'Normal', altered: 'Alterado', not_done: 'Não realizado' };
  return map[v] || v;
}

function escAttr(v: unknown) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildPosturalAssessmentPrintHtml(posture: PhysioPostureAssessment): string {
  const photos = POSTURAL_PHOTO_VIEWS
    .map((view) => {
      const photo = posture.photos?.[view.key];
      if (!photo?.file_url) return '';
      return `<div style="width:48%;margin:0 1% 10px 0;display:inline-block;vertical-align:top;">
        <div style="font-size:10px;font-weight:700;color:#334155;margin-bottom:4px;">${esc(view.label)}</div>
        <img src="${escAttr(photo.file_url)}" alt="${escAttr(view.label)}" style="width:100%;max-height:220px;object-fit:contain;border:1px solid #e2e8f0;border-radius:6px;" />
      </div>`;
    })
    .join('');

  const findings = [
    row('Achados — visão lateral', labelsFromPosturalKeys(posture.lateral_findings || [], POSTURAL_LATERAL_FINDINGS)),
    row('Achados — visão posterior', labelsFromPosturalKeys(posture.posterior_findings || [], POSTURAL_POSTERIOR_FINDINGS)),
    row('Joelhos e pés — visão anterior', labelsFromPosturalKeys(posture.anterior_lower_body || [], POSTURAL_ANTERIOR_LOWER_BODY)),
    row('Assimetria de quadril', posturalAsymmetryLabel(posture.hip_asymmetry || '')),
    row('Ombros assimétricos', posturalAsymmetryLabel(posture.shoulder_asymmetry || '')),
    row('Observações', posture.observations),
  ].join('');

  const photosBlock = photos
    ? `<div style="margin:0 0 10px;">${photos}</div>`
    : '';

  if (!photosBlock && !findings.trim()) return '';
  return `<div style="margin:0 0 14px;">
    <div style="font-size:11px;font-weight:800;color:#0f766e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;border-bottom:2px solid #ccfbf1;padding-bottom:4px;">14. Avaliação postural</div>
    ${photosBlock}
    <table style="width:100%;border-collapse:collapse;font-size:11px;">${findings}</table>
  </div>`;
}

function buildInitialPrintBody(r: PhysioInitialResponses, assessmentDate: string) {
  const id = r.identification;
  let html = section('1. Identificação', [
    row('Data da avaliação', assessmentDate),
    row('Nome completo', id.full_name),
    row('Data de nascimento', id.birth_date),
    row('Idade', id.age),
    row('Sexo', id.gender),
    row('Profissão', id.profession),
    row('Telefone', id.phone),
    row('Peso', id.weight_kg ? `${id.weight_kg} kg` : ''),
    row('Altura', id.height_m ? `${id.height_m} m` : ''),
    row('IMC', id.imc ? `${id.imc} kg/m²` : ''),
    row('Classificação IMC', id.imc_classification),
  ].join(''));

  const ant = r.anthropometry;
  const skinfolds = ant.skinfolds.filter((s) => s.result_mm).map((s) => `${s.location}: ${s.result_mm} mm`).join('; ');
  html += section('2. Avaliação antropométrica', [
    row('Circunferência abdominal', ant.abdominal_circ_cm ? `${ant.abdominal_circ_cm} cm` : ''),
    row('Circunferência da cintura', ant.waist_circ_cm ? `${ant.waist_circ_cm} cm` : ''),
    row('Circunferência do quadril', ant.hip_circ_cm ? `${ant.hip_circ_cm} cm` : ''),
    row('RCQ (cintura/quadril)', ant.waist_hip_ratio),
    row('Risco RCQ', ant.waist_hip_risk),
    row('Risco metabólico (cintura)', ant.waist_risk),
    row('Σ pregas cutâneas', ant.skinfold_sum_mm ? `${ant.skinfold_sum_mm} mm` : ''),
    row('% gordura (estimativa)', ant.body_fat_percent ? `${ant.body_fat_percent} %` : ''),
    row('Pregas cutâneas', skinfolds),
    row('Observações', ant.notes),
  ].join(''));

  const goals = labelsFromKeys(r.complaint.specific_goals, PHYSIO_SPECIFIC_GOALS);
  html += section('3. Queixa e objetivos', [
    row('Queixa principal', r.complaint.main_complaint),
    row('Objetivo principal', r.complaint.main_goal),
    row('Objetivos específicos', goals + (r.complaint.specific_goal_other ? `; ${r.complaint.specific_goal_other}` : '')),
    row('Tarefas com dificuldade', r.complaint.difficult_tasks.filter(Boolean).map((t, i) => `${i + 1}. ${t}`).join(' | ')),
  ].join(''));

  html += section('4. História clínica', [
    row('HMA', r.clinical_history.current_illness),
    row('História pregressa', r.clinical_history.past_history),
    row('Cirurgias prévias', r.clinical_history.prior_surgeries),
    row('Lesões/traumas', r.clinical_history.prior_injuries),
    row('Exames complementares', r.clinical_history.complementary_exams),
  ].join(''));

  const meds = r.medications.filter((m) => m.medication || m.dosage).map((m) =>
    `${m.medication}${m.dosage ? ` — ${m.dosage}` : ''}${m.frequency ? ` (${m.frequency})` : ''}${m.purpose ? ` — ${m.purpose}` : ''}`,
  ).join('<br/>');
  if (meds) html += section('5. Medicamentos', row('Medicamentos', meds));

  html += section('6. Sinais vitais', [
    row('Pressão arterial', r.vital_signs.blood_pressure ? `${r.vital_signs.blood_pressure} mmHg` : ''),
    row('FC', r.vital_signs.heart_rate ? `${r.vital_signs.heart_rate} bpm` : ''),
    row('SpO₂', r.vital_signs.spo2 ? `${r.vital_signs.spo2} %` : ''),
    row('Ausculta', r.vital_signs.auscultation),
  ].join(''));

  html += section('7. Dor', [
    row('Presença de dor', r.pain.has_pain === 'yes' ? 'Sim' : r.pain.has_pain === 'no' ? 'Não' : ''),
    row('Intensidade END', r.pain.intensity_end),
    row('Localização', r.pain.location),
    row('Característica', r.pain.characteristics),
    row('Observações', r.pain.notes),
  ].join(''));

  const admMap: Record<string, string> = { preserved: 'Preservada', reduced: 'Reduzida', increased: 'Aumentada', not_evaluated: 'Não avaliada' };
  const admRows = r.mobility.rows.filter((x) => x.joint_segment || x.movement).map((x) =>
    `${x.joint_segment} / ${x.movement}: D ${x.right || '—'} · E ${x.left || '—'}${x.notes ? ` (${x.notes})` : ''}`,
  ).join('<br/>');
  html += section('8. Mobilidade / ADM', [
    row('ADM geral', admMap[r.mobility.general_adm] || ''),
    row('Detalhes', admRows),
  ].join(''));

  const strengthRows = r.strength.rows.filter((x) => x.right || x.left || x.notes).map((x) =>
    `${x.muscle_group}: D ${x.right || '—'} · E ${x.left || '—'}${x.notes ? ` (${x.notes})` : ''}`,
  ).join('<br/>');
  if (strengthRows) html += section('9. Força muscular', row('Grupos', strengthRows));

  html += section('10. Coordenação', [
    row('Index-Nariz', triStateLabel(r.coordination.index_nose)),
    row('Prono-Supino', triStateLabel(r.coordination.prono_supino)),
    row('LemoCot', triStateLabel(r.coordination.lemocot)),
  ].join(''));

  html += section('11. Equilíbrio', [
    row('Base aberta', r.balance.open_base),
    row('Romberg', r.balance.romberg),
    row('Tandem', r.balance.tandem),
    row('Unipodal D', r.balance.unipodal_right),
    row('Unipodal E', r.balance.unipodal_left),
    row('TUG', r.balance.tug_seconds ? `${r.balance.tug_seconds} s` : ''),
  ].join(''));

  html += section('12. Marcha', [
    row('Caminhada 4 m', r.gait.walk_4m_seconds ? `${r.gait.walk_4m_seconds} s` : ''),
    row('Velocidade', r.gait.gait_speed_ms ? `${r.gait.gait_speed_ms} m/s` : ''),
    row('Classificação', r.gait.classification),
    row('Análise', r.gait.analysis),
  ].join(''));

  html += section('13. Testes funcionais', [
    row('Sentar/levantar 5x', r.functional_tests.sit_stand_5x_seconds ? `${r.functional_tests.sit_stand_5x_seconds} s` : ''),
    row('Degrau 2 min', r.functional_tests.step_2min_reps ? `${r.functional_tests.step_2min_reps} rep` : ''),
  ].join(''));

  html += buildPosturalAssessmentPrintHtml(r.posture);

  html += section('15. Impressão fisioterapêutica', [
    row('Alterações', r.impression.main_alterations),
    row('Limitações funcionais', r.impression.functional_limitations),
    row('Fatores de risco', r.impression.risk_factors),
  ].join(''));

  html += section('16. Objetivos terapêuticos', [
    row('Curto prazo', r.therapeutic_goals.short_term),
    row('Médio prazo', r.therapeutic_goals.medium_term),
    row('Longo prazo', r.therapeutic_goals.long_term),
    row('Metas funcionais', r.therapeutic_goals.functional_goals),
  ].join(''));

  const modalities = labelsFromKeys(r.therapeutic_plan.modalities, PHYSIO_THERAPY_MODALITIES);
  html += section('17. Plano terapêutico', [
    row('Modalidades', modalities + (r.therapeutic_plan.modality_other ? `; ${r.therapeutic_plan.modality_other}` : '')),
    row('Frequência', r.therapeutic_plan.frequency_per_week ? `${r.therapeutic_plan.frequency_per_week} vezes/semana` : ''),
    row('Orientações', r.therapeutic_plan.guidance),
  ].join(''));

  return html;
}

function buildReEvalPrintBody(r: PhysioReEvaluationResponses, assessmentDate: string) {
  const adherenceMap: Record<string, string> = { good: 'Boa', regular: 'Regular', low: 'Baixa' };
  let html = section('1. Identificação da reavaliação', [
    row('Data', assessmentDate),
    row('Período de tratamento', r.identification.treatment_period),
    row('Adesão', adherenceMap[r.identification.adherence] || ''),
    row('Peso atual', r.identification.current_weight_kg ? `${r.identification.current_weight_kg} kg` : ''),
    row('Altura atual', r.identification.current_height_m ? `${r.identification.current_height_m} m` : ''),
    row('IMC atual', r.identification.current_imc ? `${r.identification.current_imc} kg/m²` : ''),
  ].join(''));

  const comparisonRows = r.comparison.filter((x) => x.initial || x.current || x.evolution).map((x) =>
    row(x.indicator, `Inicial: ${x.initial || '—'} | Atual: ${x.current || '—'} | Evolução: ${x.evolution || '—'}`),
  ).join('');
  if (comparisonRows) html += section('2. Comparação dos resultados', comparisonRows);

  const funcRows = r.functional_evolution.filter((x) => x.goal_activity).map((x) => {
    const statusMap: Record<string, string> = { achieved: 'Alcançado', partial: 'Parcial', not_achieved: 'Não alcançado' };
    return row(x.goal_activity, `Inicial: ${x.initial_assessment || '—'} | Reavaliação: ${x.reevaluation || '—'} | ${statusMap[x.status] || ''}`);
  }).join('');
  if (funcRows) html += section('3. Evolução funcional', funcRows);

  const anthroRows = r.anthropometry_comparison.filter((x) => x.initial || x.current || x.evolution).map((x) =>
    row(x.indicator, `Inicial: ${x.initial || '—'} | Atual: ${x.current || '—'} | Evolução: ${x.evolution || '—'}`),
  ).join('');
  if (anthroRows) html += section('4. Avaliação antropométrica', anthroRows);

  const evoMap: Record<string, string> = { excellent: 'Excelente', good: 'Boa', partial: 'Parcial', none: 'Sem evolução', worse: 'Piora' };
  const conduct = labelsFromKeys(r.conclusion.conduct, PHYSIO_CONDUCT_OPTIONS);
  html += section('5. Conclusão', [
    row('Evolução clínica', evoMap[r.conclusion.clinical_evolution] || ''),
    row('Ganhos', r.conclusion.main_gains),
    row('Limitações', r.conclusion.remaining_limitations),
    row('Objetivos atingidos', r.conclusion.achieved_goals),
    row('Novos objetivos', r.conclusion.new_goals),
    row('Conduta', conduct),
    row('Observações', r.conclusion.notes),
  ].join(''));

  return html;
}

export function buildPhysiotherapyAssessmentPrintHtml(printData: any): string {
  const assessment = printData?.assessment || {};
  const clinic = printData?.clinic || assessment.clinic_info || {};
  const patient = assessment.patient || assessment.patient_snapshot || {};
  const responses = assessment.responses || {};
  const isReEval = assessment.assessment_type === 're_evaluation';
  const assessmentDate = assessment.assessment_date
    ? new Date(`${assessment.assessment_date}T12:00:00`).toLocaleDateString('pt-BR')
    : '';

  const patientBlock = `<div style="margin:0 0 12px;font-size:11px;">
    <strong>Paciente:</strong> ${esc(patient.full_name || '')}
    ${patient.cpf ? ` · CPF: ${esc(patient.cpf)}` : ''}
    ${patient.birth_date ? ` · Nasc.: ${esc(patient.birth_date)}` : ''}
  </div>`;

  const bodyHtml = isReEval
    ? buildReEvalPrintBody(responses as PhysioReEvaluationResponses, assessmentDate)
    : buildInitialPrintBody(responses as PhysioInitialResponses, assessmentDate);

  return buildClinicPrintDocumentHtml({
    clinic,
    browserTitle: isReEval ? 'Reavaliação Fisioterapêutica' : 'Avaliação Fisioterapêutica',
    badgeText: isReEval ? 'REAVALIAÇÃO FISIOTERAPÊUTICA' : 'AVALIAÇÃO FISIOTERAPÊUTICA',
    subtitleBelowBadge: assessment.assessment_number ? `Nº ${assessment.assessment_number}` : 'Estúdio Forma',
    bodyHtml: `${patientBlock}${bodyHtml}`,
  });
}
